import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

/**
 * 🚨 KILL SWITCH — Acil Durdurma
 *
 * Tüm açık pozisyonları tek tuşla kapatır. Kazara tıklamayı önlemek için
 * onay modal'ında "KAPAT" yazılması zorunludur.
 *
 * Sadece admin rolü için görünür ve sadece >0 açık pozisyon varken aktif olur.
 */
export default function KillSwitchButton({ openCount = 0, isAdmin = false }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const qc = useQueryClient();

  const killMut = useMutation({
    mutationFn: () => api.killSwitch({ confirm: "KAPAT", reason }),
    onSuccess: (data) => {
      const n = data?.closed_count || 0;
      const ok = data?.success_count || 0;
      const err = data?.error_count || 0;
      const net = data?.total_net_pnl;
      const netStr = typeof net === "number"
        ? `${net >= 0 ? "+" : ""}$${net.toFixed(2)}`
        : "";
      if (err > 0) {
        toast.warning(
          `Kill-switch tamam: ${ok}/${n} başarılı, ${err} hatalı (manuel kontrol et). Net ${netStr}`,
          { duration: 12000 },
        );
      } else if (n === 0) {
        toast.info("Açık pozisyon yoktu — kill-switch boşa tetiklendi.");
      } else {
        toast.success(
          `🚨 KILL SWITCH: ${n} pozisyon kapatıldı. Net K/Z ${netStr}`,
          { duration: 8000 },
        );
      }
      qc.invalidateQueries({ queryKey: ["positions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["exchange-positions"] });
      reset();
    },
    onError: (e) => {
      toast.error(errMsg(e, "Kill switch hatası"));
    },
  });

  const reset = () => {
    setOpen(false);
    setConfirmText("");
    setReason("");
  };

  if (!isAdmin) return null;

  const disabled = openCount === 0;
  const ready = confirmText.trim().toUpperCase() === "KAPAT";

  return (
    <>
      <Button
        type="button"
        data-testid="kill-switch-btn"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={[
          "h-9 px-4 text-xs uppercase tracking-wider font-bold rounded-sm",
          "border transition-all duration-150",
          disabled
            ? "bg-zinc-900 border-zinc-800 text-zinc-600 cursor-not-allowed"
            : "bg-red-600/15 border-red-600/40 text-red-300 hover:bg-red-600 hover:text-white hover:border-red-500 hover:shadow-[0_0_24px_rgba(239,68,68,.45)]",
        ].join(" ")}
      >
        <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
        Acil Durdurma · {openCount}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          data-testid="kill-switch-modal"
          onClick={(e) => { if (e.target === e.currentTarget) reset(); }}
        >
          <div className="relative w-full max-w-md mx-4 bg-[#0a0a0a] border border-red-600/40 rounded-md shadow-[0_0_60px_rgba(239,68,68,.25)]">
            <button
              type="button"
              onClick={reset}
              className="absolute top-3 right-3 text-zinc-500 hover:text-white"
              data-testid="kill-switch-close"
              aria-label="kapat"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-5 border-b border-red-600/20">
              <div className="flex items-center gap-2 text-red-400 font-bold uppercase tracking-wider text-sm">
                <AlertTriangle className="w-5 h-5" />
                Acil Durdurma · Onay Gerekli
              </div>
              <p className="mt-3 text-zinc-300 text-sm leading-relaxed">
                <b className="text-white">{openCount}</b> açık pozisyonun{" "}
                <b className="text-red-300">tamamı</b> hemen kapatılacak.
                Borsa tarafında her iki bacak da paralel kapanır. <br /><br />
                <span className="text-zinc-500">
                  Bu işlem geri alınamaz. Min-hold süresine bakılmaz.
                  Loss Recovery bonus uygunluğu kaybedilebilir.
                </span>
              </p>
            </div>

            <div className="p-5 space-y-3">
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-zinc-400">
                  Onaylamak için <b className="text-red-300">KAPAT</b> yaz
                </span>
                <input
                  type="text"
                  autoFocus
                  data-testid="kill-switch-confirm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && ready && !killMut.isPending) {
                      e.preventDefault();
                      killMut.mutate();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      reset();
                    }
                  }}
                  placeholder="KAPAT"
                  className="mt-1.5 w-full px-3 py-2 bg-black border border-zinc-800 rounded-sm text-white font-mono text-sm uppercase tracking-widest focus:border-red-500 focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="text-xs uppercase tracking-wider text-zinc-400">
                  Sebep (opsiyonel — log&apos;a yazılır)
                </span>
                <input
                  type="text"
                  data-testid="kill-switch-reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="örn: anormal piyasa, API down, manuel müdahale"
                  maxLength={200}
                  className="mt-1.5 w-full px-3 py-2 bg-black border border-zinc-800 rounded-sm text-zinc-300 text-sm focus:border-zinc-600 focus:outline-none"
                />
              </label>
            </div>

            <div className="p-5 border-t border-zinc-900 flex gap-2 justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={reset}
                className="h-9 px-4 text-xs uppercase tracking-wider text-zinc-400 hover:text-white"
                data-testid="kill-switch-cancel"
              >
                Vazgeç
              </Button>
              <Button
                type="button"
                data-testid="kill-switch-confirm"
                disabled={!ready || killMut.isPending}
                onClick={() => killMut.mutate()}
                className={[
                  "h-9 px-5 text-xs uppercase tracking-wider font-bold rounded-sm border",
                  ready && !killMut.isPending
                    ? "bg-red-600 border-red-500 text-white hover:bg-red-500 shadow-[0_0_20px_rgba(239,68,68,.5)]"
                    : "bg-zinc-900 border-zinc-800 text-zinc-600 cursor-not-allowed",
                ].join(" ")}
              >
                {killMut.isPending ? "Kapatılıyor..." : "🚨 Tümünü Kapat"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
